import React, { createContext, useContext, useReducer, useEffect } from 'react';

const AuthContext = createContext();

// Auth actions
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  VERIFY_TOKEN: 'VERIFY_TOKEN'
};

// Initial state - start with no token check
const initialState = {
  user: null,
  token: null, // Don't get from localStorage initially
  featureConfig: {}, // Add feature configuration
  isAuthenticated: false,
  isAuthLoading: false,
  isLoading: true,
  error: null
};

// Auth reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        isAuthLoading: true,
        error: null
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        featureConfig: action.payload.featureConfig || {}, // Include feature config
        isAuthenticated: true,
        isLoading: false, // Explicitly set to false
        isAuthLoading: false,
        error: null
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        featureConfig: {}, // Clear feature config on failure
        isAuthenticated: false,
        isLoading: false,
        isAuthLoading: false,
        error: action.payload
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        featureConfig: {}, // Clear feature config on logout
        isAuthenticated: false,
        isLoading: false,
        isAuthLoading: false,
        error: null
      };

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };

    case AUTH_ACTIONS.VERIFY_TOKEN:
      if (action.payload.success) {
        return {
          ...state,
          user: action.payload.user,
          token: action.payload.token, // Include token in state
          featureConfig: action.payload.featureConfig || {}, // Include feature config
          isAuthenticated: true,
          isLoading: false,
          isAuthLoading: false,
          error: null
        };
      } else {
        return {
          ...state,
          user: null,
          token: null,
          featureConfig: {}, // Clear feature config if verification fails
          isAuthenticated: false,
          isLoading: false,
          isAuthLoading: false,
          error: null
        };
      }

    default:
      return state;
  }
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // API request helper with authentication
  const apiRequest = async (url, options = {}) => {
    const token = state.token || localStorage.getItem('authToken');
    
    const config = {
      ...options,
      headers: {
        ...(options.headers || {}),
        'Content-Type': 'application/json',
        ...(token && !options.headers?.Authorization && {
          Authorization: `Bearer ${token}`,
        }),
      },
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (response.status === 401) {
        localStorage.removeItem('authToken');
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
        throw new Error('Session expired. Please login again.');
      }

      return { response, data };
    } catch (error) {
      throw error;
    }
  };

  // Fetch feature configuration
  const fetchFeatureConfig = async () => {
    try {
      const { data } = await apiRequest('/api/configuration/features');
      return data.success ? data.data : {};
    } catch (error) {
      console.warn('Failed to fetch feature configuration:', error);
      return {}; // Return empty config if fetch fails
    }
  };

  // Login function
  const login = async (username, password) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const { data } = await apiRequest('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password })
      });

      if (data.success) {
        localStorage.setItem('authToken', data.data.token);
        
        // Fetch feature configuration after successful login
        const featureConfig = await fetchFeatureConfig();
        
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            user: data.data.user,
            token: data.data.token,
            featureConfig: featureConfig
          }
        });
        return { success: true };
      } else {
        localStorage.removeItem('authToken');
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: data.error || 'Login failed'
        });
        return { success: false, error: data.error };
      }
    } catch (error) {
      localStorage.removeItem('authToken');
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: error.message || 'Network error'
      });
      return { success: false, error: error.message };
    }
  };

  // Logout function
  const logout = async () => {
    localStorage.removeItem('authToken');
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  };

  // Verify token on app load
  const verifyToken = async () => {
    const token = localStorage.getItem('authToken');
    
    if (!token) {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      return;
    }

    try {
      const { data } = await apiRequest('/api/auth/verify');
      
      if (data.success) {
        // Fetch feature configuration after successful token verification
        const featureConfig = await fetchFeatureConfig();
        
        dispatch({
          type: AUTH_ACTIONS.VERIFY_TOKEN,
          payload: {
            success: true,
            user: data.data?.user,
            token: token,
            featureConfig: featureConfig
          }
        });
      } else {
        dispatch({
          type: AUTH_ACTIONS.VERIFY_TOKEN,
          payload: { success: false }
        });
      }
    } catch (error) {
      console.warn('Token verification failed:', error);
      localStorage.removeItem('authToken');
      dispatch({
        type: AUTH_ACTIONS.VERIFY_TOKEN,
        payload: { success: false }
      });
    }
  };

  // Helper function to check if a feature is enabled
  const isFeatureEnabled = (featureName) => {
    return state.featureConfig[featureName] === true;
  };

  // Helper function to get feature configuration
  const getFeatureConfig = (featureName) => {
    return state.featureConfig[featureName];
  };

  // Other functions remain the same...
  const changePassword = async (currentPassword, newPassword, confirmPassword) => {
    try {
      const { data } = await apiRequest('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({
          currentPassword,
          newPassword,
          confirmPassword
        })
      });

      return {
        success: data.success,
        message: data.message,
        error: data.error
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to change password'
      };
    }
  };

  const getCurrentUser = async () => {
    try {
      const { data } = await apiRequest('/api/auth/me');
      
      if (data.success) {
        return { success: true, user: data.data.user };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Only verify token on mount, not on every state change
  useEffect(() => {
    verifyToken();
  }, []); // Empty dependency array

  const value = {
    ...state,
    login,
    logout,
    changePassword,
    getCurrentUser,
    apiRequest,
    verifyToken,
    isFeatureEnabled,
    getFeatureConfig
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export default AuthContext;